"use client";
import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MessageCircle, ShieldCheck, TriangleAlert } from "lucide-react";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import {
  failureModes,
  guaranteeNote,
  harvestingEvidence,
  rescueStages,
  rewriteNote,
  vibeCodeRescueFaqs,
} from "@/content/vibe-code-rescue";
import { whatsappHref } from "@/lib/whatsapp";

// Vibe Code Rescue. The guarantee wording in content/vibe-code-rescue.ts is a public
// commercial commitment — read that file's header before changing any of it, and do
// not soften it, extend it to the audit, or attach a price.

export default function VibeCodeRescue() {
  const locale = useLocale();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5 },
      };
  // Hero and the band right under it: moves only, opacity stays 1 so they
  // paint before hydration (LCP).
  const heroIn = reduce
    ? {}
    : {
        initial: { y: 12 },
        whileInView: { y: 0 },
        viewport: { once: true },
        transition: { duration: 0.5 },
      };
  const lp = (href: string) => getLocalizedPathname(href, locale);
  const contact = lp("/contact/");

  return (
    <main className="bg-background">
      {/* Two-column hero. The illustration is IN THE BODY, not just wired as the
          OG image — the same correction Jazz made on the AI consulting lifecycle
          page. A service hub with no visual reads as unfinished. */}
      <section className="pb-14 pt-24 lg:pt-32">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8">
          <motion.div {...heroIn} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Services")} · {t(locale, "Vibe Code Rescue")}
            </p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-6xl">
              {t(locale, "It worked in the demo. It breaks in production.")}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-muted md:text-xl">
              {t(
                locale,
                "We audit and repair software built by prompting an AI rather than engineering it. First we tell you what is actually wrong and whether it is worth fixing. Then we fix it against criteria agreed in writing — and if we do not meet them, you do not pay for the fix.",
              )}
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link
                href={contact}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Book an audit")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-8 py-3 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
              >
                {t(locale, "How the two stages work")}
              </a>
            </div>
          </motion.div>

          <motion.figure {...heroIn} className="lg:justify-self-end">
            <Image
              src="/images/services/vibe-code-rescue-editorial.jpg"
              alt={t(
                locale,
                "Editorial illustration: a small building presentable from the front while the other side stands open, showing exposed framing and loose wiring being braced and repaired",
              )}
              width={1200}
              height={800}
              priority
              className="w-full rounded-lg border border-border shadow-lg"
            />
          </motion.figure>
        </div>
      </section>

      {/* Failure modes — the reader recognising themselves */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...heroIn} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Four ways these applications fail")}
          </motion.h2>
          <motion.p {...heroIn} className="mt-3 max-w-3xl text-neutral-muted">
            {t(
              locale,
              "None of these show up in a demo. All of them show up once real people are using it.",
            )}
          </motion.p>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {failureModes.map((f) => (
              <motion.div {...heroIn} key={f.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, f.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.body)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The two stages */}
      <section id="how-it-works" className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Two stages, and the second one is guaranteed")}
          </motion.h2>
          <div className="mt-8 space-y-6">
            {rescueStages.map((s) => (
              <motion.div {...inView} key={s.number} className="rounded-lg border border-border bg-card p-6 sm:p-8">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
                  <span className="font-display text-sm font-bold text-primary">{s.number}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-neutral-emphasis">
                      {t(locale, s.name)}
                    </h3>
                    <p className="mt-1 font-medium text-neutral-emphasis">{t(locale, s.outcome)}</p>
                  </div>
                </div>
                <p className="mt-4 leading-relaxed text-neutral-muted">{t(locale, s.detail)}</p>
                <ul className="mt-5 space-y-2">
                  {s.deliverables.map((d) => (
                    <li key={d} className="flex gap-3 text-sm leading-relaxed text-neutral-muted">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                      {t(locale, d)}
                    </li>
                  ))}
                </ul>
                {s.number === "02" && (
                  <p className="mt-4 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, "Where the stack supports it, we run")}{" "}
                    <Link
                      href={lp("/enterprise/sonarqube-malaysia/")}
                      className="text-primary hover:underline"
                    >
                      {t(locale, "SonarQube for AI-written code")}
                    </Link>{" "}
                    {t(
                      locale,
                      "across the fix, so the checks that closed out the criteria keep catching regressions after we hand it back.",
                    )}
                  </p>
                )}
                <p className="mt-5 rounded-md border border-border bg-neutral-surface px-4 py-3 text-sm font-medium text-neutral-emphasis">
                  {t(locale, s.commercial)}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div {...inView} className="mt-8 rounded-lg border border-primary/30 bg-primary/5 p-6 sm:p-8">
            <p className="flex items-center gap-2 font-display text-lg font-bold text-neutral-emphasis">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
              {t(locale, "Meet the criteria, or no fee for the fix")}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-muted">{t(locale, guaranteeNote)}</p>
          </motion.div>
        </div>
      </section>

      {/* Why exposed secrets are no longer theoretical */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, harvestingEvidence.heading)}
          </motion.h2>
          <motion.p {...inView} className="mt-4 leading-relaxed text-neutral-muted">
            {t(locale, harvestingEvidence.body)}
          </motion.p>
          {/* Their evidence above, our inference below — kept visibly separate. */}
          <motion.p {...inView} className="mt-4 leading-relaxed text-neutral-muted">
            {t(locale, harvestingEvidence.ourPoint)}
          </motion.p>
          <motion.p {...inView} className="mt-4 text-sm text-neutral-muted">
            <a
              href={harvestingEvidence.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {t(locale, harvestingEvidence.sourceLabel)}
            </a>
          </motion.p>
        </div>
      </section>

      {/* Rewrite honesty */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6 sm:flex-row sm:p-8">
            <TriangleAlert className="h-6 w-6 flex-none text-primary" aria-hidden />
            <div>
              <h2 className="font-display text-xl font-bold text-neutral-emphasis md:text-2xl">
                {t(locale, rewriteNote.heading)}
              </h2>
              <p className="mt-3 leading-relaxed text-neutral-muted">{t(locale, rewriteNote.body)}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <StructuredData type="faq" faqs={vibeCodeRescueFaqs} />
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Vibe code rescue — common questions")}
          </motion.h2>
          <div className="mt-8 space-y-6">
            {vibeCodeRescueFaqs.map((f) => (
              <motion.div {...inView} key={f.question} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, f.question)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.answer)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis">
            {t(locale, "Send us the repository, or just tell us what breaks")}
          </motion.h2>
          <motion.p {...inView} className="mx-auto mt-4 max-w-2xl text-neutral-muted">
            {t(
              locale,
              "A rough idea of the size of the application and what it touches is enough to quote the audit. You do not need to tidy anything first — we would rather see it as it is.",
            )}
          </motion.p>
          <motion.div {...inView} className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href={contact}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {t(locale, "Book an audit")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <a
              href={whatsappHref("Hi Anchor Sprint, I need help rescuing an AI-built app (from your Vibe Code Rescue page).")}
              target="_blank"
              rel="noopener noreferrer"
              data-track-event="contact_whatsapp_click"
              data-track-location="vibe_code_rescue_cta"
              data-track-phone="601159246128"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              {t(locale, "WhatsApp us")}
            </a>
          </motion.div>
          <motion.p {...inView} className="mt-8 text-sm text-neutral-muted">
            <Link href={lp("/academy/")} className="text-primary hover:underline">
              {t(locale, "Vibe Engineering courses")}
            </Link>
            {" · "}
            <Link href={lp("/blog/ai-agent-rescue-fix-broken-agent-malaysia/")} className="text-primary hover:underline">
              {t(locale, "Fixing a broken AI agent")}
            </Link>
            {" · "}
            <Link href={lp("/services/software-development/")} className="text-primary hover:underline">
              {t(locale, "Software development")}
            </Link>
          </motion.p>
        </div>
      </section>
    </main>
  );
}
